import type { LargeTrade } from "@/api/polymarketApi";

export const tradeUsd = (t: LargeTrade): number => t.size * t.price;

export interface WhaleMarket {
	conditionId: string;
	title: string;
	buys: number;
	sells: number;
	trades: number;
	wallets: number;
	largest: number;
}

export interface WhaleWallet {
	wallet: string;
	label: string | null;
	usd: number;
	trades: number;
	markets: number;
	largest: number;
}

export function byMarket(trades: LargeTrade[]): WhaleMarket[] {
	const map = new Map<string, WhaleMarket & { who: Set<string> }>();
	for (const t of trades) {
		let m = map.get(t.condition_id);
		if (!m) {
			m = { conditionId: t.condition_id, title: t.title, buys: 0, sells: 0, trades: 0, wallets: 0, largest: 0, who: new Set() };
			map.set(t.condition_id, m);
		}
		const usd = tradeUsd(t);
		if (t.side === "BUY") m.buys += usd;
		else m.sells += usd;
		m.trades += 1;
		m.largest = Math.max(m.largest, usd);
		m.who.add(t.proxy_wallet);
	}
	return [...map.values()]
		.map(({ who, ...m }) => ({ ...m, wallets: who.size }))
		.sort((a, b) => b.buys + b.sells - (a.buys + a.sells));
}

export function byWallet(trades: LargeTrade[]): WhaleWallet[] {
	const map = new Map<string, WhaleWallet & { where: Set<string> }>();
	for (const t of trades) {
		let w = map.get(t.proxy_wallet);
		if (!w) {
			w = { wallet: t.proxy_wallet, label: t.name || t.pseudonym || null, usd: 0, trades: 0, markets: 0, largest: 0, where: new Set() };
			map.set(t.proxy_wallet, w);
		}
		const usd = tradeUsd(t);
		w.usd += usd;
		w.trades += 1;
		w.largest = Math.max(w.largest, usd);
		w.where.add(t.condition_id);
	}
	return [...map.values()]
		.map(({ where, ...w }) => ({ ...w, markets: where.size }))
		.sort((a, b) => b.usd - a.usd);
}

export interface HolderShare {
	label: string;
	share: number;
	amount: number;
}

/**
 * Split of the holders the API returned. The public endpoint caps the list, so
 * this is concentration among those holders, not a share of all open interest.
 */
export function holderSplit(amounts: number[]): HolderShare[] {
	const sorted = [...amounts].sort((a, b) => b - a);
	const total = sorted.reduce((s, a) => s + a, 0);
	if (total <= 0) return [];
	const top1 = sorted[0] ?? 0;
	const next9 = sorted.slice(1, 10).reduce((s, a) => s + a, 0);
	const rest = total - top1 - next9;
	return [
		{ label: "Top 1", share: top1 / total, amount: top1 },
		{ label: "Top 2–10", share: next9 / total, amount: next9 },
		{ label: `Next ${Math.max(0, sorted.length - 10)}`, share: rest / total, amount: rest },
	];
}
